/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import ContactRelationship from '@linc-technologies/linced-common/enums/contact-relationship';
import MedicalRelationship from '@linc-technologies/linced-common/enums/medical-relationship';
import MedicalConditionType from '@linc-technologies/linced-common/enums/medical-condition-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import { compact, findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class ApplyController extends Controller {
  @service
  router;

  InputType = InputType;

  @tracked
  previewSchemaId = ''; // From query parameter

  @computed('previewSchemaId', 'schema.id')
  get isPreview() {
    return this.previewSchemaId === this.schema.id;
  }

  // isShowingErrors backs the validation-error dialog. It is deliberately not
  // named `showErrors`: that would collide with the `showErrors` action below,
  // which is a sibling class member now that the actions hash is gone.
  @tracked
  isShowingErrors = false;
  relationships = ContactRelationship.values;
  medicalRelationships = MedicalRelationship.values;
  medicalConditionTypes = MedicalConditionType.values;

  @computed(
    'schema.{sections.[],primaryContacts,emergencyContacts,medicalContacts,medicalConditions}',
  )
  get allSections() {
    return [
      ...this.schema.sections,
      ...compact([
        this.schema.primaryContacts,
        this.schema.emergencyContacts,
        this.schema.medicalContacts,
        this.schema.medicalConditions,
      ]),
    ];
  }

  @computed('schema.medicalConditions.fields.@each.key')
  get subtypeField() {
    if (isEmpty(this.schema.medicalConditions)) {
      return null;
    }
    return findBy(
      this.get('schema.medicalConditions.fields'),
      'key',
      'core:subtype',
    );
  }

  @computed('schema.medicalConditions.fields.@each.key')
  get epiPenField() {
    if (isEmpty(this.schema.medicalConditions)) {
      return null;
    }
    return findBy(
      this.get('schema.medicalConditions.fields'),
      'key',
      'core:autoinjectorHeldOnsite',
    );
  }

  @computed('schema.medicalConditions.fields.@each.key')
  get extraMedicalConditionFields() {
    if (isEmpty(this.schema.medicalConditions)) {
      return null;
    }
    return this.get('schema.medicalConditions.fields').filter((field) => {
      return ![
        'core:subtype',
        'core:actionPlans',
        'core:autoinjectorHeldOnsite',
      ].includes(field.key);
    });
  }

  @tracked
  savingApplication = false;

  @tracked
  currentStep;

  @action
  changeStep(index) {
    this.currentStep = index;
    window.location.href = '#steps';
  }

  @action
  setContactRelationship(contact, relationship) {
    contact.set(
      'relationship',
      isEmpty(relationship) ? null : relationship.value,
    );
  }

  @action
  setMedicalRelationship(contact, medicalRelationship) {
    contact.set(
      'medicalRelationship',
      isEmpty(medicalRelationship) ? null : medicalRelationship.value,
    );
  }

  @action
  setMedicalConditionType(condition, type) {
    condition.set('type', isEmpty(type) ? null : type.value);
  }

  @action
  submitApplication() {
    if (this.isPreview) {
      // Don't actually allow submission of previews.
      return;
    }
    this.savingApplication = true;
    let schema = this.schema;
    // Save the application
    if (!isEmpty(this.applicationRecord)) {
      this.applicationRecord
        .save()
        .then(() => {
          // Transition to happy screen.
          this.router.transitionTo(
            'apply-complete',
            schema.countryCode,
            schema.schoolCode,
          );
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.savingApplication = false;
        });
    }
  }

  @action
  showErrors() {
    this.isShowingErrors = true;
  }

  @action
  hideErrors() {
    this.isShowingErrors = false;
  }
}
